import { Box, Card, type CardProps, Group, LoadingOverlay, Stack, Title } from '@mantine/core';
import type React from 'react';
import { isDefined, isNonNullish } from 'remeda';

interface PanelProps
  extends CardProps,
    React.PropsWithChildren<{
      title?: React.ReactNode;
      subtitle?: React.ReactNode;
      topRight?: React.ReactNode;
      topLeft?: React.ReactNode;
      footer?: React.ReactNode;
      isLoading?: boolean;
      flushContent?: boolean;
      fullHeight?: boolean;
      // Pins the footer to the bottom of the card by letting the content region
      // grow. Only useful when the card is taller than its content (e.g. a
      // `fullHeight` card stretched to match a taller sibling). Off by default.
      pinFooter?: boolean;
    }> {}

export const Panel = ({
  title,
  subtitle,
  topRight,
  topLeft,
  children,
  footer,
  isLoading = false,
  flushContent = false,
  fullHeight = false,
  pinFooter = false,
  ...rest
}: PanelProps) => {
  const shouldShowHeader = isDefined(title) || isDefined(topRight) || isDefined(topLeft);

  return (
    <Card
      py="space8"
      px={flushContent ? 0 : 'space6'}
      pt={flushContent ? (shouldShowHeader ? 'space7' : 0) : 'space7'}
      pb={flushContent || isNonNullish(footer) ? 0 : 'space6'}
      bdrs={16}
      {...rest}
      style={{ borderWidth: 1.5, height: fullHeight ? '100%' : undefined, ...rest.style }}
    >
      {shouldShowHeader && (
        <Card.Section
          pb="space3"
          withBorder={rest.withBorder}
          px={flushContent ? 'space6' : 0}
          mx={-24}
          mb={flushContent ? 0 : 'space4'}
        >
          <Group px="space6" justify="space-between">
            <Stack gap={0}>
              <Group>
                {isDefined(title) && (
                  <Title fw={400} order={6} fz={17}>
                    {title}
                  </Title>
                )}
                {topLeft != null && topLeft}
              </Group>
              {subtitle}
            </Stack>
            {topRight != null && topRight}
          </Group>
        </Card.Section>
      )}
      {pinFooter ? <Box style={{ flexGrow: 1 }}>{children}</Box> : children}
      {/* mb={0} cancels the negative margin Card.Section gives a last child, so the
          footer owns its own symmetric padding and the action sits centred between
          the divider and the card edge. */}
      {isNonNullish(footer) && (
        <Card.Section mt="space5" withBorder mx={-24} mb={0} py="space3" px="space6">
          <Group justify="end">{footer}</Group>
        </Card.Section>
      )}
      <LoadingOverlay visible={isLoading} overlayProps={{ radius: 'sm', blur: 2 }} />
    </Card>
  );
};
